<template>
    <div @click="closeClickToEditComponents">
        <alert v-if="hasInvalidAttributes && !readOnly" :text="$t('shared.auction_data_viewer.consider_red_marks')" :headline="$t('shared.auction_data_viewer.warning')" class="mb-5"></alert>
        <div class="font-medium border-b-2 border-gray-200 mb-4" v-if="showHeadlines">{{$t('shared.auction_data_viewer.auction_attributes')}}</div>
        <div class="grid auto-cols-max gap-2 mb-4" :class="gridColStyle">
            <div class="auction-attribute-container" v-if="hasField('article_id')">
                <span class="text-base md:text-xl">{{ auction.article_id }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.article_number')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="hasField('article_id')">
                <span class="text-base md:text-xl">{{ auction.name }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.name')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="hasField('article_quantity')">
                <span class="text-base md:text-xl">
                <click-to-edit
                    @update="valueUpdated"
                    @state="stateChanged"
                    :forceClose="forceClickToEditClose"
                    :allowed="canBeEdited(auction) && !readOnly"
                    :type="`number`"
                    :name="`article_quantity`"
                    :min="1"
                    :step="1"
                    :max="context.max_quantity ? context.max_quantity + auction.article_quantity : auction.article_quantity"
                    :origin="auction.article_quantity"
                    :valid="isQuantityValid">
                    {{ auction.article_quantity }} ({{ context.max_quantity }})
                </click-to-edit>
                </span>
                <span class="block mt-1 text-sm">{{$t('shared.auction_data_viewer.planned_amount')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.start_price && hasField('start_price')">
                <span class="text-base md:text-xl">
                    <click-to-edit
                        @update="valueUpdated"
                        @state="stateChanged"
                        @changed="auctionPriceChangeCallback"
                        :forceClose="forceClickToEditClose"
                        :allowed="canBeStarted(auction) && !readOnly"
                        :type="`number`"
                        :required="true"
                        :name="`start_price`"
                        :step="this.constraintAuctionStartPriceStep"
                        :max="auction.start_price === 0 ? auction.attributes.reference_price : (this.canBeEdited(auction) ? auction.attributes.reference_price : auction.start_price)"
                        :min="2"
                        :origin="auction.start_price"
                        :valid="isStartPriceValid">
                        {{ auction.start_price | toCurrency }}
                    </click-to-edit>
                </span>
                <span class="block mt-1 text-sm">{{$t('shared.auction_data_viewer.starting_price')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.end_price && hasField('end_price')">
                <span class="text-base md:text-xl">{{ auction.end_price | toCurrency(2) }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.final_price')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="parseInt(auction.pricedrop_interval) >= 0 && hasField('pricedrop_interval')">
                <span class="text-base md:text-xl">
                    <click-to-edit
                        @update="valueUpdated"
                        @state="stateChanged"
                        :forceClose="forceClickToEditClose"
                        :allowed="canBeStarted(auction) && !readOnly"
                        :type="`number`"
                        :name="`pricedrop_interval`"
                        :min="(auction.start_price <= 10) ? 7 : 15"
                        :max="899"
                        :origin="auction.pricedrop_interval"
                        :valid="isPriceDropIntervalValid">
                        {{ auction.pricedrop_interval }}s
                    </click-to-edit>
                </span>
                <span class="block mt-1 text-sm">{{$t('shared.auction_data_viewer.price_change_interval')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.pricedrop_amount >= 0 && hasField('pricedrop_amount')">
                <span class="text-base md:text-xl">
                    <click-to-edit
                        @update="valueUpdated"
                        @state="stateChanged"
                        :forceClose="forceClickToEditClose"
                        :allowed="canBeStarted(auction) && !readOnly"
                        :type="`number`"
                        :name="`pricedrop_amount`"
                        :step="(auction.start_price > 10) ? 1 : 0.5"
                        :min="(auction.start_price > 10) ? 1 : 0.5"
                        :max="(auction.start_price <= 10) ? 0.5 : 100"
                        :origin="auction.pricedrop_amount"
                        :valid="isPriceDropAmountValid">
                        {{ auction.pricedrop_amount | toCurrency(1) }}
                    </click-to-edit>
                </span>
                <span class="block mt-1 text-sm">{{$t('shared.auction_data_viewer.price_change_amount')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.attributes.reference_price && hasField('attribute_reference_price')">
                <span class="text-base md:text-xl">{{ auction.attributes.reference_price | toCurrency }}</span><br/>
                <span class="text-xs md:text-sm">Regulärer Preis</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.attributes.purchase_price && hasField('attribute_purchase_price')">
                <span class="text-base md:text-xl">{{ auction.attributes.purchase_price | toCurrency }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.wholesale_price')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.auction_min_price && hasField('auction_min_price')">
                <span class="text-base md:text-xl">{{ auction.auction_min_price | toCurrency(2) }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.auction_min_price')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.attributes.avg && hasField('attribute_avg')">
                <span class="text-base md:text-xl">{{ auction.attributes.avg | toCurrency }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.avg')}}</span>
            </div>
            <div class="auction-attribute-container" :class="{'has-error': this.isCurrentPriceHigherThanWebshopPrice}" v-if="auction.attributes.webshop_price && hasField('webshop_price')">
                <span class="text-base md:text-xl">
                    <template v-if="auction.attributes.webshop_price !== null && auction.attributes.webshop_price !== undefined">
                        {{ auction.attributes.webshop_price | toCurrency(2) }}
                    </template>
                    <template v-else>
                        n.a.
                    </template>
                </span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.webshop_price')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.amount_bid_min && hasField('amount_bid_min')">
                <span class="text-base md:text-xl">{{ auction.amount_bid_min | toCurrency }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.lowest_bid')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="this.showDynamicAmountBidMin && hasField('dynamic_amount_bid_min')">
                <span class="text-base md:text-xl">{{ runtime.dynamic_amount_bid_min | toCurrency }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.dynamic_lowest_bid')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.amount_bid_max && hasField('amount_bid_max')">
                <span class="text-base md:text-xl">{{ auction.amount_bid_max | toCurrency }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.max_bid')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.quantity_bids_allowed && hasField('quantity_bids_allowed')">
                <span class="text-base md:text-xl">{{ auction.quantity_bids_allowed }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.max_bid_count_per_customer')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.quantity_articles_per_bid && hasField('quantity_articles_per_bid')">
                <span class="text-base md:text-xl">{{ auction.quantity_articles_per_bid }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.max_amount_per_bid')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.attributes.category && hasField('attribute_category')">
                <span class="text-base md:text-xl">{{ auction.attributes.category }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.ecnh')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.attributes.highlight !== false && hasField('attribute_highlight')">
                <span class="text-base md:text-xl">{{ auction.attributes.highlight === 1 ? 'Ja' : 'Nein' }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.highlight_auction')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.attributes.category && hasField('attribute_category')">
                <span class="text-base md:text-xl">{{ auction.attributes.category }}</span><br/>
                <span class="text-xs md:text-sm">ECNH</span>
            </div>
        </div>
        <div class="font-medium border-b-2 border-gray-200 mb-4" v-if="showHeadlines">Timing</div>
        <div class="grid auto-cols-max mb-4 gap-2" :class="gridColStyle">
            <div class="auction-attribute-container" v-if="hasField('auction_start_plan')">
                <span class="text-base md:text-xl">{{ auction.date_start | formatDate }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.auction_start_plan')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.date_started && hasField('date_started')">
                <span class="text-base md:text-xl">{{ auction.date_started | formatDate }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.auction_start_actual')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.date_end && hasField('date_end')">
                <span class="text-base md:text-xl">{{ auction.date_end | formatDate }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.auction_end_plan')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="auction.date_ended && hasField('date_ended')">
                <span class="text-base md:text-xl">{{ auction.date_ended | formatDate }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.auction_end_actual')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="hasField('preview_duration')">
                <click-to-edit
                    @update="valueUpdated"
                    @state="stateChanged"
                    :forceClose="forceClickToEditClose"
                    :allowed="canBeEdited(auction) && !readOnly"
                    :type="`number`"
                    :name="`preview_duration`"
                    :step="1"
                    :min="0"
                    :origin="auction.preview_duration">
                    <span class="text-base md:text-xl" v-if="auction.preview_duration > 0">{{ auction.preview_duration }}s</span>
                    <span class="text-base md:text-xl" v-else-if="auction.preview_duration === 0">{{$t('shared.auction_data_viewer.manual')}}</span>
                    <span class="text-base md:text-xl" v-else>{{$t('shared.auction_data_viewer.deactivated')}}</span>
                </click-to-edit>
                <span class="block mt-1 text-sm">{{$t('shared.auction_data_viewer.forward')}}</span>
            </div>
            <div class="auction-attribute-container" v-if="hasField('trail_duration')">
                <click-to-edit
                    @update="valueUpdated"
                    @state="stateChanged"
                    :forceClose="forceClickToEditClose"
                    :allowed="(canBeStopped(auction) || canBeStarted(auction)) && !readOnly"
                    :type="`number`"
                    :name="`trail_duration`"
                    :step="1"
                    :min="5"
                    :origin="auction.trail_duration">
                    <span class="text-base md:text-xl">{{ auction.trail_duration }}s</span>
                </click-to-edit>
                <span class="block mt-1 text-sm">{{$t('shared.auction_data_viewer.follow_up')}}</span>
            </div>
        </div>
        <div class="font-medium border-b-2 border-gray-200 mb-4" v-if="showHeadlines">{{$t('shared.auction_data_viewer.master_data')}}</div>
        <div class="grid auto-cols-max mb-4 gap-2" :class="gridColStyle">
            <div class="flex-1 auction-attribute-container" v-if="hasField('created_at')">
                <span class="text-base md:text-xl">{{ auction.created_at | formatDate }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.creation')}}</span>
            </div>
            <div class="flex-1 auction-attribute-container" v-if="hasField('updated_at')">
                <span class="text-base md:text-xl">{{ auction.updated_at | formatDate }}</span><br/>
                <span class="text-xs md:text-sm">{{$t('shared.auction_data_viewer.last_update')}}</span>
            </div>
        </div>
    </div>
</template>

<script>
    import ClickToEdit from "@/Shared/ClickToEdit";
    import AuctionState from "@/mixins/AuctionState";
    import Alert from "@/Shared/Alert";
    export default {
        mixins: [
            AuctionState
        ],
        components: {
            Alert,
            ClickToEdit,
        },
        props: {
            auction: Object,
            runtime: Object,
            context: {
                type: Object,
                default: () => { return {} }
            },
            readOnly: {
                type: Boolean,
                default: true
            },
            showHeadlines: {
                type: Boolean,
                default: true
            },
            fields: {
                type: Array,
                default: []
            },
            cols: {
                type: Number,
                default: 3
            }
        },
        data() {
          return {
              activeClickToEdits: 0,
              forceClickToEditClose: false,
              clickToEditState: {
                  start_price: {
                      new: null,
                      old: null
                  }
              }
          }
        },
        mounted() {
            this.$emit('auction-validated', !this.hasInvalidAttributes);
        },
        computed: {
            gridColStyle() {
              return {
                  'grid-cols-2 md:grid-cols-2': this.cols === 2,
                  'grid-cols-2 md:grid-cols-3': this.cols === 3,
                  'grid-cols-2 md:grid-cols-4': this.cols === 4,
                  'grid-cols-2 md:grid-cols-5': this.cols === 5,
                  'grid-cols-2 md:grid-cols-6': this.cols === 6,
                  'grid-cols-2 md:grid-cols-7': this.cols === 7,
              }
            },
            isStartPriceValid() {
                return this.auction.start_price > 0;
            },
            isPriceDropAmountValid() {
                return this.auction.pricedrop_amount > 0;
            },
            isQuantityValid() {
                return this.auction.article_quantity > 0;
            },
            isPriceDropIntervalValid() {
                return this.auction.pricedrop_interval > 0;
            },
            isCurrentPriceHigherThanWebshopPrice() {
                if (this.hasRuntime()) {
                    return this.runtime.current_price > this.auction.attributes.webshop_price;
                }
                return this.auction.start_price > this.auction.attributes.webshop_price;
            },
            hasInvalidAttributes() {
                return !this.isStartPriceValid ||
                    !this.isPriceDropAmountValid ||
                    !this.isQuantityValid ||
                    !this.isPriceDropIntervalValid
            },
            constraintAuctionStartPriceStep() {
                if (this.clickToEditState.start_price.new > 10) {
                    return 1;
                }
                if (this.clickToEditState.start_price.new < 10) {
                    return  0.5;
                }
                if (this.clickToEditState.start_price.new === 10) {
                    return this.clickToEditState.start_price.old > this.clickToEditState.start_price.new ? 0.5 : 1;
                }
            },
            showDynamicAmountBidMin() {
                return this.auction.auction_type_id === 1 && this.hasRuntime() && (this.auction.type === 'T' || this.auction.type === 'P');
            }
        },
        methods: {
            hasField(field) {
              return this.fields.length === 0 || this.fields.includes(field);
            },
            closeClickToEditComponents() {
                if (this.activeClickToEdits > 0) {
                    this.forceClickToEditClose = true;
                }
            },
            stateChanged(val) {
                if (val) {
                    this.activeClickToEdits++;
                } else {
                    this.activeClickToEdits--;
                }
                if (0 === this.activeClickToEdits) {
                    this.forceClickToEditClose = false;
                }
            },
            valueUpdated(val) {
              this.$emit('auction-update', val);
              this.closeClickToEditComponents();
            },
            auctionPriceChangeCallback(val) {
              this.clickToEditState.start_price = {
                  new: isNaN(val.new) ? this.clickToEditState.start_price.new : val.new,
                  old: isNaN(val.old) ? this.clickToEditState.start_price.old : val.old,
              };
            },
            hasRuntime() {
                return this.runtime !== null;
            }
        },
        watch: {
            hasInvalidAttributes(val) {
                this.$emit('auction-validated', !this.hasInvalidAttributes);
            }
        }
    }
</script>
